/**
 * DuplicatePlanModal — duplica un plan existente y lo reasigna a otro cliente.
 *
 * Flujo:
 *  1. El entrenador hace clic en "Duplicar" sobre un plan (ej. el de Camila).
 *  2. Este modal pide elegir cliente destino (lista existente).
 *  3. Al confirmar: leemos el plan fuente completo, hacemos deep-clone de
 *     `sections` y `brand`, y re-aplicamos los prefill helpers con los datos
 *     del cliente destino (cover, ficha, calculadora nutricional, medidas).
 *  4. Se crea un plan nuevo en backend; el coach puede abrirlo y ajustar lo
 *     que considere (cambiar un ejercicio, ajustar cantidades, etc.).
 *
 * Lo que se mantiene del plan original:
 *   - Toda la rutina (ejercicios, series, reps, semanas).
 *   - Suplementos.
 *   - Cardio.
 *   - Notas/secciones de texto.
 *   - Estilo visual (brand).
 *
 * Lo que se recalcula con el cliente destino:
 *   - `cover.clientName`
 *   - `client.*` (nombre, edad, peso, altura, objetivo, lesiones)
 *   - `diet.calculator` (TDEE, déficit, macros) — basado en peso/altura/edad/sexo
 *   - `measurements` (toma inicial con datos del cliente)
 *
 * El nombre del plan se inserta como `"<nombre original> — <cliente destino>"`.
 */
import { useEffect, useMemo, useState } from "react";
import { Icon } from "../../lib/Icon";
import { useToast } from "../../lib/toast";
import { getClient, listClients, type ClientFull } from "../../api/clients";
import { listMeasurements } from "../../api/measurements";
import {
  createPlan,
  getPlan,
  type PlanListItem,
} from "../../api/plans";
import {
  prefillMeasurementsSection,
  prefillSectionsFromClient,
} from "./prefillFromClient";
import { applyBrandCoverDefaults, brandFromUser, type PlanDoc } from "../../types/plan";
import { applyTemplateStyleOnly, getTemplate, TEMPLATES, type PlanTemplate } from "../../types/templates";
import { useAuth } from "../../contexts/AuthContext";

interface DuplicatePlanModalProps {
  source: PlanListItem;
  onClose: () => void;
  /** Llamado tras crear el plan nuevo. Se usa para refrescar la lista. */
  onDuplicated: (newPlanId: number, destClient: ClientFull) => void;
}

export function DuplicatePlanModal({ source, onClose, onDuplicated }: DuplicatePlanModalProps) {
  const toast = useToast();
  const { user } = useAuth();
  const [clients, setClients] = useState<ClientFull[] | null>(null);
  const [destId, setDestId] = useState<number | null>(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Plantilla destino opcional. null = mantener la plantilla del plan origen.
  // Caso de uso: dupliqué plantilla rosa de mujer para usarla con un cliente
  // hombre → quiero cambiar también a una plantilla con portada masculina.
  const [destTemplateId, setDestTemplateId] = useState<string | null>(null);
  const [showTplPicker, setShowTplPicker] = useState(false);

  useEffect(() => {
    listClients()
      .then(setClients)
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, []);

  // Si el plan ya está asignado a un cliente, NO lo ofrecemos como destino
  // (duplicarlo encima de sí mismo no tiene sentido — el coach usaría "editar").
  const filtered = useMemo(() => {
    if (!clients) return [];
    const lower = q.trim().toLowerCase();
    return clients
      .filter((c) => c.id !== source.client_id)
      .filter((c) => !lower || c.full_name.toLowerCase().includes(lower));
  }, [clients, q, source.client_id]);

  useEffect(() => {
    // Escape cierra. Si está procesando, no.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const doDuplicate = async () => {
    if (destId == null || busy) return;
    setBusy(true);
    setErr(null);
    try {
      // Paso 1: traemos el plan completo (con sections + brand).
      const src = await getPlan(source.id);

      // Paso 2: cliente destino + sus medidas (para recalcular cover/ficha/macros).
      const [destClient, destMeasurements] = await Promise.all([
        getClient(destId),
        listMeasurements(destId).catch(() => []), // si falla, seguimos sin medidas
      ]);

      // Paso 3: deep-clone del cuerpo del plan. JSON-round-trip es suficiente
      // porque sections/brand son data plana (sin Dates, sin funciones).
      // PlanDoc requiere campos meta (`id`/`createdAt`/...) que en duplicación
      // no aplican — usamos placeholders porque los prefill helpers solo leen
      // `sections` (revisamos `prefillFromClient.ts`).
      const cloned: PlanDoc = {
        id: `dup_${Date.now()}`,
        name: src.name,
        template: src.template,
        brand: src.brand ? JSON.parse(JSON.stringify(src.brand)) : null,
        sections: JSON.parse(JSON.stringify(src.sections || [])),
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      // Paso 4: re-prefill con el cliente destino. Esto sobreescribe cover,
      // client, diet.calculator y measurements. La rutina, suplementos, cardio
      // y demás quedan idénticos.
      prefillSectionsFromClient(cloned, destClient, destMeasurements);
      prefillMeasurementsSection(cloned, destClient, destMeasurements);

      // El prefill deja el inicio del plan VACÍO → el calendario no se ve en el
      // duplicado. Seteamos inicio = HOY para que el calendario aparezca de una;
      // el coach lo ajusta a la fecha real de arranque del cliente.
      {
        const d = new Date();
        const todayIso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        const cli = cloned.sections.find((s) => s.type === "client");
        if (cli && cli.data && typeof cli.data === "object") {
          (cli.data as { startDate?: string }).startDate = todayIso;
        }
      }

      // Paso 4.5 (opcional): swap visual a otra plantilla. Cambia portada/
      // paleta/tipografía/theme sin tocar el contenido. Útil cuando duplicás
      // un plan femenino para un cliente masculino y querés look distinto.
      const finalPlan = destTemplateId
        ? applyTemplateStyleOnly(cloned, getTemplate(destTemplateId)!)
        : cloned;

      // Paso 4.6: la portada hereda los defaults de "Mi marca" del coach
      // (eyebrow/subtítulo/duración). Mergea: lo que el coach definió en su
      // marca pisa lo que venía del plan/plantilla origen; lo que no, se
      // mantiene. Mismo helper que usa el alta de plan.
      applyBrandCoverDefaults(finalPlan, user);

      // Paso 4.7 (SEGURIDAD): la IDENTIDAD de marca (nombre/logo/contacto)
      // SIEMPRE es la del entrenador que duplica — nunca la del plan origen.
      // Evita que un plan duplicado muestre el nombre/datos de OTRO entrenador.
      // Los colores/tema/watermark se respetan (swap de plantilla u origen).
      const myBrand = brandFromUser(user);
      if (myBrand) {
        finalPlan.brand = {
          ...(finalPlan.brand || {}),
          name: myBrand.name,
          tagline: myBrand.tagline,
          logoLetters: myBrand.logoLetters,
          logoUrl: myBrand.logoUrl,
          ig: myBrand.ig,
          web: myBrand.web,
          phone: myBrand.phone,
        };
      }

      // Paso 5: crear plan nuevo. El nombre incluye el destino para que el
      // coach lo distinga de inmediato en la lista; igual lo puede renombrar.
      const baseName = (source.name || "Plan").trim();
      const newName = `${baseName} — ${destClient.full_name}`;
      const created = await createPlan({
        name: newName,
        template: finalPlan.template,
        client_id: destClient.id,
        client_name: destClient.full_name,
        sections: finalPlan.sections,
        brand: finalPlan.brand,
      });

      toast(`Plan duplicado para ${destClient.full_name}`);
      onDuplicated(created.id, destClient);
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(msg);
      toast(`No pude duplicar: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="al-modal-backdrop" onClick={() => !busy && onClose()}>
      <div
        className="al-modal al-dup-plan"
        role="dialog"
        aria-label="Duplicar plan a otro cliente"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="al-modal-hd">
          <div>
            <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18 }}>
              Duplicar plan
            </h3>
            <div className="tiny muted" style={{ marginTop: 2 }}>
              Origen: <b>{source.name || "Sin título"}</b>
              {source.client_name ? <> · {source.client_name}</> : null}
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Cerrar" disabled={busy}>
            <Icon name="x" size={16} />
          </button>
        </div>

        <div className="al-dup-plan-body">
          <p className="tiny muted" style={{ margin: 0 }}>
            Elegí el cliente destino. Vamos a copiar la rutina, el cardio, los
            suplementos y el estilo — y a <b>recalcular</b> los datos del cliente,
            las macros y la toma de medidas con los valores del cliente nuevo.
          </p>

          <div className="al-dup-plan-search">
            <Icon name="search" size={14} />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar cliente por nombre…"
              autoFocus
              disabled={busy}
            />
          </div>

          {clients == null && !err && (
            <div className="muted" style={{ padding: 20 }}>
              Cargando lista de clientes…
            </div>
          )}

          {err && (
            <div className="al-dup-plan-err">
              No pude cargar los clientes: {err}
            </div>
          )}

          {clients && filtered.length === 0 && (
            <div className="muted" style={{ padding: 20, textAlign: "center" }}>
              {q.trim() ? `No hay clientes que coincidan con "${q}".` : "No tenés otros clientes disponibles."}
            </div>
          )}

          {clients && filtered.length > 0 && (
            <div className="al-dup-plan-list">
              {filtered.map((c) => (
                <label
                  key={c.id}
                  className={"al-dup-plan-row" + (destId === c.id ? " is-selected" : "")}
                >
                  <input
                    type="radio"
                    name="dup-dest"
                    value={c.id}
                    checked={destId === c.id}
                    onChange={() => setDestId(c.id)}
                    disabled={busy}
                  />
                  <div className="al-dup-plan-row-info">
                    <div className="al-dup-plan-row-name">{c.full_name}</div>
                    <div className="tiny muted">
                      {c.sex === "F" ? "Mujer" : c.sex === "M" ? "Hombre" : "—"}
                      {c.current_weight_kg != null ? ` · ${c.current_weight_kg} kg` : ""}
                      {c.height_cm != null ? ` · ${c.height_cm} cm` : ""}
                      {c.goal ? ` · ${c.goal}` : ""}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}

          {/* Sección colapsable: cambiar la plantilla visual al duplicar.
              Default OFF — el coach generalmente quiere mantener el mismo
              look. Si está duplicando para "el otro sexo" o quiere cambiar
              colores, expande y elige. */}
          <div className="al-dup-plan-tpl-section">
            <button
              type="button"
              className="al-dup-plan-tpl-toggle"
              onClick={() => setShowTplPicker((v) => !v)}
              disabled={busy}
            >
              <Icon name="grid" size={12} />
              {destTemplateId
                ? <>Cambiar a: <b>&nbsp;{getTemplate(destTemplateId)?.label || "plantilla seleccionada"}</b></>
                : <>+ También cambiar la plantilla visual <span className="muted">(opcional)</span></>}
              <span className="al-dup-plan-tpl-caret">{showTplPicker ? "▲" : "▼"}</span>
            </button>
            {showTplPicker && (
              <>
                <p className="tiny muted" style={{ margin: "4px 0 8px", lineHeight: 1.4 }}>
                  Mantiene rutina/dieta/cardio del plan original — solo cambia
                  la portada, paleta y tipografía. Útil cuando duplicás un plan
                  femenino para un cliente masculino y querés look distinto.
                </p>
                <div className="al-dup-plan-tpl-list">
                  <button
                    type="button"
                    className={"al-dup-plan-tpl-opt" + (destTemplateId === null ? " is-selected" : "")}
                    onClick={() => setDestTemplateId(null)}
                    disabled={busy}
                  >
                    <span className="al-dup-plan-tpl-opt-mark">=</span>
                    <span>
                      <b>Mantener la actual</b>
                      <span className="tiny muted" style={{ display: "block" }}>
                        Usa la misma plantilla que el plan original
                      </span>
                    </span>
                  </button>
                  {TEMPLATES.filter((t) => t.id !== source.template).map((t: PlanTemplate) => {
                    const isOn = destTemplateId === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        className={"al-dup-plan-tpl-opt" + (isOn ? " is-selected" : "")}
                        onClick={() => setDestTemplateId(t.id)}
                        disabled={busy}
                        style={{ borderColor: isOn ? t.palette.primary : undefined }}
                      >
                        <span
                          className="al-dup-plan-tpl-opt-swatch"
                          style={{ background: t.palette.primary, color: "#fff" }}
                        >
                          {t.label.slice(0, 2).toUpperCase()}
                        </span>
                        <span>
                          <b>{t.label}</b>
                          <span className="tiny muted" style={{ display: "block" }}>
                            <span style={{ color: t.palette.primary, fontWeight: 700 }}>{t.category}</span> · {t.description}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="al-dup-plan-foot">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={doDuplicate}
            disabled={destId == null || busy}
          >
            {busy ? "Duplicando…" : (
              <>
                <Icon name="check" size={14} /> Duplicar para este cliente
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
